import { useMemo } from "react";
import { useAuth } from "../services/authService";
import {
  FilterOptions,
  View,
  QueryParams,
  Query,
} from "../helpers/interfaceHelper";
import { isValidFilterOption } from "../helpers/dataHelper";

/*
 * Constructs an object storing the various query parameters that should be passed to the API to retrieve data.
 * Includes things like property to sort on, sort direction etc.
 */

const useQuery: (params: QueryParams) => any = ({
  sortProperty,
  sortDirection,
  filters,
  page,
  rowsPerPage,
  search,
  view,
  views,
}) => {
  const { getLoggedInName } = useAuth();
  return useMemo((): Query => {
    const specialFilters: any = {};
    //if we have received views back from the database, then we need to try and find
    //the chosen view, and set up the filters accordingly.
    if (views) {
      let chosenView: View | undefined;
      //first try and find the view based on the ID provided. This should work in cases where
      //the user has chosen a view
      chosenView = views.find((v) => v.id.toString() === view.toString());

      //if we didn't find a view based on the ID, then we try and find one based on the name
      //of the view. This should only really be relevant for first time loading of the page,
      //as we don't know what ID the default view will have in the database
      if (chosenView === undefined) {
        chosenView = views.find((v) => v.name === view);
      }

      //if the list of views contains the chosen one, then we need to parse the json
      //and construct the filters
      if (
        typeof chosenView === "object" &&
        chosenView !== null &&
        chosenView !== undefined
      ) {
        const jsonOptions: FilterOptions = JSON.parse(
          chosenView.jsonFilterOptions
        );
        //for each of the keys on the json object, we want to map them through
        //to the relevant filter option
        Object.keys(jsonOptions).forEach(function (key, index) {
          switch (key) {
            //if the key is status not in we need to do two things:
            //1) we want to just map the value straight through to the status not in filter
            //2) we want to map the value through to the status filter - this is used for the selected options on the ui
            case "status_not_in":
              specialFilters[key] = jsonOptions[key];
              specialFilters["status_name"] = { $nin: jsonOptions[key] };
              break;

            //if the key is self as agent we need to check a couple of things:
            //1) if the value is false, we don't need to do anything
            //2) if the filters already have an agent_name key, we want to append the current user's name to the list of agents
            //3) if the filters don't have an agent_name key yet, we set it up as a new array including the user's name
            case "self_as_agent":
              if (jsonOptions[key] === true) {
                var loggedInName = getLoggedInName();
                if (loggedInName) {
                  if (filters.hasOwnProperty("agent_name")) {
                    filters.agent_name.push(loggedInName);
                  } else {
                    filters.agent_name = [loggedInName];
                  }
                }
              }
              break;

            //if the key is agent_name, we need to perform similar checks to above:
            //1) if the filters already have an agent_name key, we want to append each of these values to the list of agents
            //2) if the filters don't have an agent_name key, we just set up the key as the array of agents provided
            case "agent_name":
              if (filters.hasOwnProperty("agent_name")) {
                jsonOptions[key].forEach(function (item: any) {
                  filters.agent_name.push(item);
                });
              } else {
                filters.agent_name = jsonOptions[key];
              }
              break;

            //otherwise, just set the key up with the values from the json
            default:
              if (isValidFilterOption(key, jsonOptions)) {
                if (!filters.hasOwnProperty(key)) {
                  specialFilters[key] = jsonOptions[key];
                }
              }
              break;
          }
        });
      }
    }

    if (search) {
      specialFilters.custom_search = search;
    }

    return {
      $skip: page * rowsPerPage,
      $limit: rowsPerPage,
      $sort: {
        [sortProperty]: sortDirection,
      },
      ...filters,
      ...specialFilters,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    sortProperty,
    sortDirection,
    page,
    rowsPerPage,
    filters,
    search,
    view,
    views,
  ]);
};

export default useQuery;
